import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Headphones } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import usePodcasts, { type PodcastWithRelations } from '@/hooks/usePodcasts';
import { useCategories } from '@/hooks/useCategories';
import { useTags } from '@/hooks/useTags';
import type { Categories, Tags } from '@/lib/supabase-types';
import { PodcastList } from './components/PodcastList';
import { PodcastHero } from './components/PodcastHero';
import { PodcastCategories } from './components/PodcastCategories';
import { FeaturedEpisode } from './components/FeaturedEpisode';
import { PodcastEditModal } from '@/components/cms/PodcastEditModal';
import { AudioPlayer } from './components/AudioPlayer';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { AdminModeToggle } from '@/components/AdminModeToggle';
import { ImageBrowser } from '@/components/media/ImageBrowser';
import { AudioBrowser } from '@/components/media/AudioBrowser';
import { AudioUploader } from '../../components/cms/AudioUploader';
import { PlayButton } from './components/PlayButton';
import { useLocation } from 'react-router-dom';

export const Podcasts = () => {
  const { category } = useParams<{ category?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPodcast, setEditingPodcast] = useState<PodcastWithRelations | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tags[]>([]);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [showAudioBrowser, setShowAudioBrowser] = useState(false);
  const isCmsPage = location.pathname === '/cms';

  const { 
    currentPodcast,
    isPlaying,
    loadingAudio,
    handlePlayPause,
    audioControls
  } = useAudioPlayer();

  const { 
    loading: podcastsLoading, 
    error: podcastsError, 
    podcasts,
    setPodcasts,
    createPodcast,
    updatePodcast,
    deletePodcast,
    fetchPodcasts
  } = usePodcasts();

  const { loading: categoriesLoading, categories } = useCategories();
  const { loading: tagsLoading, tags } = useTags();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    loadData();
  }, [category]);

  const loadData = async () => {
    try {
      const data = await fetchPodcasts();
      setPodcasts(data);
    } catch (error) {
      console.error('Error loading podcasts:', error);
    }
  };

  const handleDeletePodcast = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este podcast?')) {
      try {
        await deletePodcast(id);
        const updatedPodcasts = await fetchPodcasts();
        setPodcasts(updatedPodcasts);
        toast.success('Podcast excluído com sucesso!');
      } catch (error) {
        console.error('Error deleting podcast:', error);
        toast.error('Erro ao excluir podcast');
      }
    }
  };

  const handleCreatePodcast = () => {
    const defaultCategory = categories.find(c => c.slug === 'latest') || categories[0];
    
    if (!defaultCategory) {
      console.error('No categories available');
      return;
    }

    setEditingPodcast({
      id: '',
      title: '',
      description: '',
      category_id: defaultCategory.id,
      audio_url: '',
      image_url: '',
      author: '',
      duration: '',
      plays: 0,
      published_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      slug: '',
      category: defaultCategory,
      tags: []
    });
    setSelectedTags([]);
  };

  const handleEditPodcast = (podcast: PodcastWithRelations) => {
    setEditingPodcast(podcast);
    setSelectedTags(podcast.tags || []);
  };

  const handleSavePodcast = async () => {
    if (!editingPodcast) return;

    try {
      let savedPodcast: PodcastWithRelations;

      if (editingPodcast.id) {
        savedPodcast = await updatePodcast(editingPodcast.id, editingPodcast);
      } else {
        savedPodcast = await createPodcast(editingPodcast);
      }

      const updatedPodcasts = await fetchPodcasts();
      setPodcastsList(updatedPodcasts);
      toast.success('Podcast salvo com sucesso!');
      setEditingPodcast(null);
      setSelectedTags([]);
    } catch (error) {
      console.error('Error saving podcast:', error);
      toast.error('Erro ao salvar podcast');
    }
  };

  if (podcastsLoading || categoriesLoading || tagsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-400">A carregar podcasts...</p>
        </div>
      </div>
    );
  }

  if (podcastsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Erro ao carregar os podcasts</p>
          <p className="text-gray-400">{podcastsError}</p>
        </div>
      </div>
    );
  }

  // Get latest episodes (sorted by publish date)
  const latestEpisodes = podcasts && podcasts.length > 0
    ? [...podcasts].sort((a, b) => new Date(b.published_at || '').getTime() - new Date(a.published_at || '').getTime()).slice(0, 5)
    : [];

  const [featuredEpisode, ...recentEpisodes] = latestEpisodes;

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background to-secondary/20">
      <PodcastHero category={category} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <PodcastCategories 
          category={category} 
          onCategoryChange={(cat) => navigate(cat ? `/podcasts/${cat}` : '/podcasts')} 
        />

        {/* Latest Episodes Section - Show when no category is selected */}
        {!category && (
          <>
            {/* Featured Episode */}
            {featuredEpisode && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold mb-8">Episódio em Destaque</h2>
                <FeaturedEpisode
                  podcast={featuredEpisode}
                  currentPodcast={currentPodcast}
                  isPlaying={isPlaying}
                  loadingAudio={loadingAudio}
                  onPlayPause={handlePlayPause}
                />
              </div>
            )}

            {/* Recent Episodes */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold mb-8">Episódios Recentes</h2>
              <div className="grid gap-8 md:grid-cols-2">
                {recentEpisodes.map((episode) => (
                  <motion.div
                    key={episode.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card rounded-xl overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all group"
                  >
                    <div
                      className="h-64 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{ backgroundImage: `url(${episode.image_url})` }}
                    />
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <span className="px-3 py-1 bg-secondary rounded-full text-sm">
                          {episode.category?.name}
                        </span>
                      </div>

                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {episode.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {episode.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <PlayButton
                          podcast={episode}
                          currentPodcast={currentPodcast}
                          isPlaying={isPlaying}
                          loadingAudio={loadingAudio}
                          onPlayPause={handlePlayPause}
                          size="sm"
                          variant="full"
                        />
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {episode.duration}
                          </span>
                          <span className="flex items-center gap-2">
                            <Headphones className="w-4 h-4" />
                            {episode.plays} plays
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* All Podcasts List */}
        <div>
          <h2 className="text-3xl font-bold mb-8">
            {category ? 'Todos os Episódios' : 'Biblioteca Completa'}
          </h2>
          <PodcastList
            podcasts={podcasts}
            category={category}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isAdminMode={isAdminMode && isCmsPage}
            currentPodcast={currentPodcast}
            isPlaying={isPlaying}
            loadingAudio={loadingAudio}
            onPlayPause={handlePlayPause}
            onEdit={handleEditPodcast}
            onDelete={handleDeletePodcast}
            onCreateNew={handleCreatePodcast}
          />
        </div>

        {currentPodcast && (
          <AudioPlayer
            podcast={currentPodcast}
            isPlaying={isPlaying}
            controls={audioControls}
            onPlayPause={handlePlayPause}
          />
        )}

        {editingPodcast && (
          <PodcastEditModal
            podcast={editingPodcast}
            categories={categories}
            tags={tags}
            selectedTags={selectedTags}
            onSave={handleSavePodcast}
            onClose={() => {
              setEditingPodcast(null);
              setSelectedTags([]);
            }}
            onChange={setEditingPodcast}
            onTagsChange={setSelectedTags}
            onImageBrowse={() => setShowImageBrowser(true)}
            onAudioBrowse={() => setShowAudioBrowser(true)}
            setActiveSection={() => {}}
            isOpen={!!editingPodcast}
          />
        )}

        {showImageBrowser && (
          <ImageBrowser
            onSelect={(url) => {
              if (editingPodcast) {
                setEditingPodcast({ ...editingPodcast, image_url: url });
              }
              setShowImageBrowser(false);
            }}
            onClose={() => setShowImageBrowser(false)}
            isAdmin={true}
          />
        )}

        {showAudioBrowser && (
          <AudioBrowser
            onSelect={(url) => {
              setEditingPodcast(prev => prev ? { ...prev, audio_url: url } : null);
              setShowAudioBrowser(false);
            }}
            onClose={() => setShowAudioBrowser(false)}
            isAdmin={true}
          />
        )}
      </div>
    </div>
  );
};